import { Check, MessageCircle, Pencil, Plus, RadioTower, Trash2, X } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "../lib/api";
import { canManageChannels, useAuth } from "../lib/auth";
import type { Channel } from "../lib/types";

type TargetType = NonNullable<Channel["targetType"]>;
type ChannelForm = Pick<Channel, "name" | "telegramId" | "targetType" | "active" | "isDefault">;

const targetConfig = {
  channel: {
    title: "Add Channel",
    editTitle: "Edit Channel",
    listTitle: "Channels",
    idLabel: "Telegram Channel ID",
    hint: "เช่น @maan88_official หรือ -1001234567890",
    placeholder: "@maan88_official",
    defaultLabel: "Default channel",
    empty: "ยังไม่มี Channel",
    added: "เพิ่ม Channel แล้ว",
    updated: "อัปเดต Channel แล้ว",
    failed: "บันทึก Channel ไม่สำเร็จ",
    icon: RadioTower
  },
  group: {
    title: "Add Group",
    editTitle: "Edit Group",
    listTitle: "Groups",
    idLabel: "Telegram Group ID",
    hint: "เช่น -1001234567890 หรือ group numeric chat id",
    placeholder: "-1001234567890",
    defaultLabel: "Default group",
    empty: "ยังไม่มี Group",
    added: "เพิ่ม Group แล้ว",
    updated: "อัปเดต Group แล้ว",
    failed: "บันทึก Group ไม่สำเร็จ",
    icon: MessageCircle
  }
} satisfies Record<TargetType, Record<string, unknown>>;

function makeEmptyForm(targetType: TargetType): ChannelForm {
  return {
    name: "",
    telegramId: "",
    targetType,
    active: true,
    isDefault: false
  };
}

function getTargetType(channel: Channel): TargetType {
  return channel.type ?? channel.targetType ?? "channel";
}

export function Channels() {
  const { user } = useAuth();
  const [items, setItems] = useState<Channel[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<TargetType | null>(null);
  const [forms, setForms] = useState<Record<TargetType, ChannelForm>>({
    channel: makeEmptyForm("channel"),
    group: makeEmptyForm("group")
  });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const groupedItems = useMemo(
    () => ({
      channel: items.filter((item) => getTargetType(item) === "channel"),
      group: items.filter((item) => getTargetType(item) === "group")
    }),
    [items]
  );

  const load = async () => {
    try {
      const result = await api.channels();
      setItems(result.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "โหลด channel ไม่สำเร็จ");
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!canManageChannels(user)) {
    return <div className="rounded border border-line bg-white p-6 text-sm text-slate-600">role นี้ไม่มีสิทธิ์จัดการ Channels</div>;
  }

  const updateForm = (targetType: TargetType, patch: Partial<ChannelForm>) => {
    setForms((current) => ({
      ...current,
      [targetType]: { ...current[targetType], ...patch, targetType }
    }));
  };

  const resetForm = (targetType: TargetType) => {
    setForms((current) => ({ ...current, [targetType]: makeEmptyForm(targetType) }));
    if (editingType === targetType) {
      setEditingId(null);
      setEditingType(null);
    }
  };

  const submit = (targetType: TargetType) => async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setNotice("");
    const isEditingThisType = editingId && editingType === targetType;
    const body = { ...forms[targetType], targetType };

    try {
      if (isEditingThisType) {
        await api.updateChannel(editingId, body);
        setNotice(targetConfig[targetType].updated as string);
      } else {
        await api.createChannel(body);
        setNotice(targetConfig[targetType].added as string);
      }
      resetForm(targetType);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (targetConfig[targetType].failed as string));
    }
  };

  const edit = (channel: Channel) => {
    const targetType = getTargetType(channel);
    setEditingId(channel.id);
    setEditingType(targetType);
    setError("");
    setNotice("");
    setForms((current) => ({
      ...current,
      [targetType]: {
        name: channel.name,
        telegramId: channel.telegramId,
        targetType,
        active: channel.active,
        isDefault: channel.isDefault
      }
    }));
  };

  const remove = async (channel: Channel) => {
    await api.deleteChannel(channel.id);
    await load();
  };

  const renderSection = (targetType: TargetType) => {
    const config = targetConfig[targetType];
    const form = forms[targetType];
    const sectionItems = groupedItems[targetType];
    const isEditingThisType = editingId && editingType === targetType;
    const Icon = config.icon as typeof RadioTower;

    return (
      <section className="grid gap-6 xl:grid-cols-[380px_1fr]">
        <form onSubmit={submit(targetType)} className="h-fit rounded border border-line bg-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="rounded bg-brand/10 p-2 text-brand">
              <Icon size={18} />
            </span>
            <div>
              <h1 className="text-xl font-semibold text-ink">{isEditingThisType ? (config.editTitle as string) : (config.title as string)}</h1>
              <p className="mt-1 text-sm text-slate-500">{config.hint as string}</p>
            </div>
          </div>

          <label className="mt-4 block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Name</span>
            <input
              value={form.name}
              onChange={(event) => updateForm(targetType, { name: event.target.value })}
              className="focus-ring w-full rounded border border-line px-3 py-2"
              required
            />
          </label>

          <label className="mt-4 block">
            <span className="mb-1 block text-sm font-medium text-slate-700">{config.idLabel as string}</span>
            <input
              value={form.telegramId}
              onChange={(event) => updateForm(targetType, { telegramId: event.target.value })}
              className="focus-ring w-full rounded border border-line px-3 py-2"
              placeholder={config.placeholder as string}
              required
            />
          </label>

          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.active} onChange={(event) => updateForm(targetType, { active: event.target.checked })} />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.isDefault} onChange={(event) => updateForm(targetType, { isDefault: event.target.checked })} />
              {config.defaultLabel as string}
            </label>
          </div>

          <div className="mt-5 flex gap-2">
            <button type="submit" className="inline-flex flex-1 items-center justify-center gap-2 rounded bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800">
              {isEditingThisType ? <Check size={16} /> : <Plus size={16} />}
              {isEditingThisType ? "Update" : "Add"}
            </button>
            {isEditingThisType ? (
              <button
                type="button"
                onClick={() => resetForm(targetType)}
                className="rounded border border-line px-3 text-slate-600 hover:bg-slate-100"
                title="Cancel"
              >
                <X size={18} />
              </button>
            ) : null}
          </div>
        </form>

        <div className="overflow-hidden rounded border border-line bg-white shadow-sm">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Telegram ID</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3">Default</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sectionItems.map((channel) => (
                <tr key={channel.id} className="border-t border-line">
                  <td className="px-4 py-3 font-medium text-ink">{channel.name}</td>
                  <td className="px-4 py-3">{channel.telegramId}</td>
                  <td className="px-4 py-3">{channel.active ? "Yes" : "No"}</td>
                  <td className="px-4 py-3">{channel.isDefault ? "Yes" : "No"}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => edit(channel)} className="rounded border border-line p-2 text-slate-600 hover:bg-slate-100" title="Edit">
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => remove(channel)}
                        className="rounded border border-red-200 p-2 text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sectionItems.length === 0 ? <div className="p-6 text-sm text-slate-500">{config.empty as string}</div> : null}
        </div>
      </section>
    );
  };

  return (
    <div className="space-y-6">
      {error ? <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {notice ? <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div> : null}
      {renderSection("channel")}
      {renderSection("group")}
    </div>
  );
}
